import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import DropdownList from 'react-widgets/DropdownList';
import ChartLine from '../../../ChartLine';
import ChartBar from '../../../ChartBar';
import CustomModal from "../../../Modal";
import config from '../../../../config';
import localStorage from '../../../../helpers/localStorage';
import { withTranslation } from '../../../ChildrenContext';
import CustomTooltip from '../../../Tooltip';

const nameLocalStorage = 'chartType';

const images = {
    bar: require('img/chart/chart-bar.svg'),
    line: require('img/chart/chart-line.svg')
};

const DashboardChart = ({
    t,
    labels = [],
    metricsData = {},
    parameters = {},
    loading = false
}) => {
    const staticNames = (t && t.chart) || {};

    if (!localStorage.get(nameLocalStorage)) {
        localStorage.set(nameLocalStorage, config.defaultChartBar);
    }

    const [chartData, setChartData] = useState({
        labels: labels,
        metricsData: metricsData
    });
    const [yAxesLeft, setYAxesLeft] = useState('Visits');
    const [yAxesRight, setYAxesRight] = useState('Visits');
    const [metrics, setMetrics] = useState([]);
    const [isVisibleModal, setIsVisibleModal] = useState(false);
    const [chartType, setChartType] = useState(localStorage.get(nameLocalStorage));

    // Update chartData when props change
    useEffect(() => {
        const isChangeData = chartData.metricsData !== metricsData;
        if (isChangeData) {
            setChartData({
                labels: labels,
                metricsData: metricsData
            });
        }
    }, [labels, metricsData, chartData.metricsData]);

    const onChangeDropdownList = useCallback((value, key) => {
        const metricKey = value && typeof value === 'object' ? value.value : value;
        if (key === 'yAxesLeft') {
            setYAxesLeft(metricKey);
        } else if (key === 'yAxesRight') {
            setYAxesRight(metricKey);
        }
    }, []);

    const onSetMetrics = useCallback((newMetrics) => {
        if (JSON.stringify(metrics) !== JSON.stringify(newMetrics)) {
            setMetrics(newMetrics);
        }
    }, [metrics]);

    const onToggleModal = useCallback(() => {
        setIsVisibleModal(prev => !prev);
    }, []);

    const handleSetChartType = useCallback((e, type) => {
        e.preventDefault();
        localStorage.set(nameLocalStorage, type);
        setChartType(type);
        setIsVisibleModal(false);
    }, []);

    const chartSwitch = useCallback((type) => {
        switch(type) {
            case config.chartLine:
                return (
                    <ChartLine
                        className={'statistics-chart'}
                        chartData={chartData}
                        yAxesLeft={yAxesLeft}
                        yAxesRight={yAxesRight}
                        parameters={parameters}
                        loading={loading}
                        getMetrics={onSetMetrics}
                        height={328}
                    />
                );
            default:
                return (
                    <ChartBar
                        className={'statistics-chart'}
                        chartData={chartData}
                        yAxesLeft={yAxesLeft}
                        yAxesRight={yAxesRight}
                        parameters={parameters}
                        loading={loading}
                        getMetrics={onSetMetrics}
                        height={328}
                    />
                );
        }
    }, [chartData, parameters, loading, yAxesLeft, yAxesRight, onSetMetrics]);

    const getChartSwitcherIcon = useCallback((type) => {
        switch(type) {
            case config.chartLine:
                return (
                    <img src={images.line} alt={staticNames.lines}/>
                );
            default:
                return (
                    <img src={images.bar} alt={staticNames.bars}/>
                );
        }
    }, [staticNames]);

    return (
        <div className="ibox chart">
            <div className="ibox-title ibox-title__included">
                <div className="table-filter table-filter_justify chart-toolbar__wrapper">
                    <div className={`chart-toolbar`}>
                        <div className="table-filter__item">
                            <DropdownList
                                className={'rw-dropdownlist-select dimension dimension__first'}
                                valueField="value"
                                textField='title'
                                data={metrics}
                                value={metrics.find(m => m.value === yAxesLeft)}
                                disabled={loading || [metrics.find(m => m.value === yAxesRight)]}
                                onChange={(value) => {
                                    if (value) onChangeDropdownList(value, 'yAxesLeft');
                                }}
                            />
                        </div>
                        <div className="table-filter__item">
                            <DropdownList
                                className={'rw-dropdownlist-select dimension dimension__second'}
                                valueField="value"
                                textField='title'
                                data={metrics}
                                value={metrics.find(m => m.value === yAxesRight)}
                                disabled={loading || [metrics.find(m => m.value === yAxesLeft)]}
                                placeholder={staticNames.noLabel}
                                onChange={(value) => {
                                    if (value) onChangeDropdownList(value, 'yAxesRight');
                                }}
                            />
                        </div>
                    </div>
                    <div className={`chart-toolbar`}>
                        <CustomTooltip id={`changeChartTypeTooltip`} tooltip={staticNames.changeChartType}>
                            <button
                                className="btn"
                                onClick={onToggleModal}
                                title={staticNames.changeChartType}
                            >
                                {getChartSwitcherIcon(chartType)}
                            </button>
                        </CustomTooltip>
                    </div>
                </div>
            </div>
            <div className="ibox-content">
                <React.Fragment>
                    <CustomModal
                        className={'settingsMenu'}
                        title={staticNames.settingsModalTitle}
                        confirmName={staticNames.applyMetrics}
                        visible={isVisibleModal}
                        areNoControls={true}
                        onClose={onToggleModal}
                    >
                        <div className="row">
                            <div className="col-xs-12 chart-types">
                                <button className={`chart-type chart-type-bar ${chartType === config.chartBar ? 'active' : ''}`} onClick={e => handleSetChartType(e, config.chartBar)}>
                                    <span>{staticNames.bars}</span>
                                </button>
                                <button
                                    className={`chart-type chart-type-line ${chartType === config.chartLine ? 'active' : ''}`}
                                    onClick={e => handleSetChartType(e, config.chartLine)}>
                                    <span>{staticNames.lines}</span>
                                </button>
                            </div>
                        </div>
                    </CustomModal>
                </React.Fragment>
                {chartSwitch(chartType)}
            </div>
        </div>
    );
};

DashboardChart.propTypes = {
    t: PropTypes.object,
    labels: PropTypes.array,
    metricsData: PropTypes.object,
    parameters: PropTypes.object,
    loading: PropTypes.bool
};

export default withTranslation(DashboardChart);
